import { useCallback, useMemo } from "react";

import { ErrorMessage } from "@/components/ui/error";
import { LoadingSpinner } from "@/components/ui/spinner";
import { useDeleteInvoice } from "@/queries/invoices/delete";
import { useGetInvoice } from "@/queries/invoices/get";
import { useGetAllLineItems } from "@/queries/line-items/fetch";

import { InvoiceReviewClientForm } from "./invoice-review-client-form";

type Props = {
  invoiceId: string;
  updateBox: (polygons?: any, fieldName?: string) => void;
};

export const InvoiceReviewClient = ({ invoiceId, updateBox }: Props) => {
  const query = useMemo(
    () => ({
      invoiceId,
    }),
    [invoiceId],
  );

  const { invoice, loading, error, refetch } = useGetInvoice(query);
  const {
    lineItems,
    loadingLineItems,
    errorLineItems,
    refetch: lineItemsRefetch,
  } = useGetAllLineItems(invoiceId);
  const deleteInvoice = useDeleteInvoice();

  const handleUpdateBox = useCallback(
    (polygons?: any, fieldName?: string) => {
      updateBox(polygons, fieldName);
    },
    [updateBox],
  );

  const handleRefetch = useCallback(() => {
    refetch();
    lineItemsRefetch();
  }, [refetch, lineItemsRefetch]);

  const handleDelete = useCallback(() => {
    deleteInvoice([invoiceId]);
  }, [deleteInvoice, invoiceId]);

  const isLoading = loading || loadingLineItems;
  const isError = error || errorLineItems;

  const payments = useMemo(() => {
    return (
      invoice?.invoiceDetails.payments.map((payment) => {
        const iban = payment.recipientName
          ? payment.recipientIban
          : invoice.invoiceDetails.supplier?.paymentDetails?.IBAN ??
            invoice.invoiceDetails.invoice.iban;

        return {
          ...payment,
          recipientName: invoice.invoiceDetails.supplier?.supplierName ?? "",
          amount: payment.amount ?? invoice.invoiceDetails.invoice.totalAmount,
          recipientIban: iban ?? null,
          currency: payment.currency ?? invoice.invoiceDetails.invoice.currency,
        };
      }) ?? []
    );
  }, [invoice]);

  const draftPayments = useMemo(() => {
    return (
      invoice?.invoiceDetails.draftPayments.map((draftPayment) => {
        const iban = draftPayment.recipientIban
          ? draftPayment.recipientIban
          : invoice.invoiceDetails.supplier?.paymentDetails?.IBAN ??
            invoice.invoiceDetails.invoice.iban;

        return {
          ...draftPayment,
          recipientName: invoice.invoiceDetails.supplier?.supplierName ?? "",
          amount: draftPayment.amount,
          recipientIban: iban ?? null,
          currency:
            draftPayment.currency ?? invoice.invoiceDetails.invoice.currency,
        };
      }) ?? []
    );
  }, [invoice]);
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError || !invoice) {
    return <ErrorMessage retry={handleRefetch} />;
  }

  return (
    <InvoiceReviewClientForm
      invoice={invoice.invoiceDetails.invoice}
      duplicates={invoice.invoiceDetails.duplicates}
      transactions={invoice.invoiceDetails.transactions}
      payments={payments}
      draftPayments={draftPayments}
      supplier={invoice.invoiceDetails.supplier}
      tags={invoice.invoiceDetails.tags}
      polygons={invoice.invoiceDetails.invoicePolygon.polygonData}
      lineItems={lineItems.map(({ lineItem }) => lineItem)}
      updateBox={handleUpdateBox}
      onDelete={handleDelete}
      disabled={false}
    />
  );
};
